import { motion } from 'framer-motion'
import { FileText, ExternalLink, Clock, X, Eye, Download } from 'lucide-react'
import { useState } from 'react'
import { DocumentPreviewModal } from '../DocumentPreviewModal'
import { api } from '../../lib/api'

interface SearchResult {
  id: string
  title: string
  content: string
  snippet: string
  relevance_score: number
  match_type: string
  metadata: any
  document_id?: string
  chunk_id?: string
}

interface SearchResultsProps {
  results: SearchResult[]
  queryTime: number
  totalCount: number
  onClose: () => void
}

export function SearchResults({ results, queryTime, totalCount, onClose }: SearchResultsProps) {
  const [previewDocument, setPreviewDocument] = useState<any>(null)

  const handleDownload = async (documentId: string, fileName: string, isReference?: boolean) => {
    try {
      // Use appropriate endpoint based on document type
      const endpoint = isReference ? 
        `/api/reference-documents/${documentId}/download` : 
        `/api/documents/${documentId}/download`
      
      const response = await api.get(endpoint, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: response.headers['content-type'] })
      const url = URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  const getMatchTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'hybrid': 'Beste Übereinstimmung',
      'semantic': 'Inhaltlich relevant',
      'fuzzy': 'Ähnlich',
      'exact_phrase': 'Exakte Phrase',
      'full_text': 'Volltext',
      'phonetic': 'Klingt ähnlich'
    }
    return labels[type] || type
  }

  const getMatchTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'exact_phrase': 'bg-green-500',
      'semantic': 'bg-blue-500',
      'fuzzy': 'bg-yellow-500',
      'phonetic': 'bg-purple-500',
      'full_text': 'bg-gray-500',
      'hybrid': 'bg-gradient-to-r from-blue-500 to-purple-500'
    }
    return colors[type] || 'bg-gray-500'
  }

  return (
    <div className="space-y-4">
      {/* Results header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-400">
          <span>{totalCount} Ergebnisse</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {queryTime.toFixed(0)}ms
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-white/10 rounded-lg transition-smooth"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Results list */}
      <div className="space-y-3">
        {results.map((result, index) => (
          <motion.div
            key={result.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`glass rounded-xl p-4 hover:bg-white/10 transition-all group ${
              result.metadata?.is_reference ? 'cursor-pointer' : ''
            }`}
            onClick={() => {
              if (result.metadata?.is_reference) {
                setPreviewDocument({
                  id: result.document_id || result.id,
                  title: result.title,
                  fileType: result.metadata?.file_type || '.pdf'
                })
              }
            }}
          >
            <div className="flex items-start gap-4">
              {/* Icon and Download Button */}
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-lg bg-primary-600/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary-400" />
                </div>
                {/* Download button below icon */}
                {result.metadata?.public && result.document_id && (
                  <button 
                    className="mt-2 w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDownload(result.document_id!, result.title, result.metadata?.is_reference)
                    }}
                    title="Herunterladen"
                  >
                    <Download className="w-4 h-4 text-primary-400" />
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-white group-hover:text-primary-400 transition-colors">
                    {result.title}
                  </h3>
                  {/* Only show match type for first result */}
                  {index === 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full text-white ${getMatchTypeColor(result.match_type)}`}>
                      {getMatchTypeLabel(result.match_type)}
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-400 line-clamp-2 mb-2">
                  {result.snippet}
                </p>

                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>
                    {Math.round(result.relevance_score * 100)}% Relevanz
                  </span>
                  {result.metadata?.is_reference && (
                    <button 
                      className="flex items-center gap-1 hover:text-primary-400 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Eye className="w-3 h-3" />
                      <span>Vorschau anzeigen</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* No results */}
      {results.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">Keine Ergebnisse gefunden</p>
          <p className="text-sm text-gray-500 mt-2">
            Versuchen Sie es mit anderen Suchbegriffen
          </p>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreviewModal
          isOpen={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          documentId={previewDocument.id}
          documentTitle={previewDocument.title}
          fileType={previewDocument.fileType}
        />
      )}
    </div>
  )
}